import { Flex, TextButton, Typography, usePopup } from '@banhala/ui-kit';
import { useAtomValue } from 'jotai';
import { Suspense, useCallback, useEffect, useRef } from 'react';
import { ActivityIndicator } from 'react-native';
import { useIsForegroundNow } from 'shared/src/hooks';
import { bridge } from 'shared/src/utils';
import { useLogClickDeleteMySize } from '../../hooks/useLogClickDeleteMySize';
import { useDeleteProductCategoryMeasureValues } from '../../queries/useDeleteProductCategoryMeasureValues';
import { useProductCategoryMeasureValuesRenderInfo } from '../../queries/useProductCategoryMeasureValuesRenderInfo';
import { useMyPersonalizationSizeAtoms } from '../../state';
import { formatMeasureValue } from '../../utils';
import { MySizeEmptyState } from './MySizeEmptyState';

export const MySizeSection = () => {
  return (
    <Flex>
      <Flex
        backgroundColor="bg_always_white"
        padding={{ horizontal: 16, vertical: 12 }}
      >
        <Typography type="h5" color="content_primary">
          내 사이즈
        </Typography>
      </Flex>
      <Suspense
        fallback={
          <Flex.Center flex={1} minHeight={200}>
            <ActivityIndicator />
          </Flex.Center>
        }
      >
        <MySizeSectionContent />
      </Suspense>
    </Flex>
  );
};

const MySizeSectionContent = () => {
  const { selectedProductCategoryAtom } = useMyPersonalizationSizeAtoms();
  const selectedProductCategory = useAtomValue(selectedProductCategoryAtom);
  const { data, refetch } = useProductCategoryMeasureValuesRenderInfo(
    selectedProductCategory.product_category_sno,
  );
  const isForeground = useIsForegroundNow();

  const isMountedRef = useRef(false);

  /**
   * 값 수정 후  재진입 시 refetch
   */
  useEffect(() => {
    if (isMountedRef.current === false) {
      isMountedRef.current = true;
      return;
    }
    if (isForeground) {
      refetch();
    }
  }, [isForeground, refetch]);

  return (
    <Flex padding={{ horizontal: 20 }}>
      {data.hasAnyValue ? (
        <Flex.Column gap={8} width="100%" alignItems="flex-start">
          <Flex.Column
            width="100%"
            borderRadius={8}
            borderColor="gray20"
            borderWidth={1}
            padding={{ horizontal: 20, vertical: 20 }}
            gap={16}
          >
            {selectedProductCategory.measure_spots.map(v => (
              <Flex.Row
                key={v.sno}
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography type="subtitle2" color="content_primary">
                  {v.name}
                </Typography>
                <Typography type="body2" color="content_secondary">
                  {formatMeasureValue(data.measureValues[v.sno] ?? 0)}
                </Typography>
              </Flex.Row>
            ))}
          </Flex.Column>
          <MySizeDeleteButton />
        </Flex.Column>
      ) : (
        <MySizeEmptyState />
      )}
    </Flex>
  );
};

const MySizeDeleteButton = () => {
  const { show: showPopup } = usePopup();

  const logClickDelete = useLogClickDeleteMySize();

  const { mutate: deleteProductCategoryMeasureValues } =
    useDeleteProductCategoryMeasureValues();
  const { selectedProductCategoryGroupAtom, selectedProductCategoryAtom } =
    useMyPersonalizationSizeAtoms();
  const selectedProductCategoryGroup = useAtomValue(
    selectedProductCategoryGroupAtom,
  );
  const selectedProductCategory = useAtomValue(selectedProductCategoryAtom);

  const handlePress = useCallback(async () => {
    logClickDelete({
      productCategorySno: selectedProductCategory.product_category_sno,
      standardCategorySno: selectedProductCategoryGroup.standard_category_sno,
      standardCategoryName: selectedProductCategoryGroup.standard_category_name,
    });
    const res = await showPopup({
      title: '내 사이즈를 삭제할게요',
      message: '삭제한 사이즈는 다시 복원할 수 없어요.',
      confirm: '확인',
      cancel: '취소',
    });
    if (res.type === 'confirm') {
      deleteProductCategoryMeasureValues(
        selectedProductCategory.product_category_sno,
        {
          onSuccess: () => {
            bridge.toast('내 사이즈가 삭제되었습니다');
          },
        },
      );
    }
  }, [
    deleteProductCategoryMeasureValues,
    logClickDelete,
    selectedProductCategory.product_category_sno,
    selectedProductCategoryGroup.standard_category_name,
    selectedProductCategoryGroup.standard_category_sno,
    showPopup,
  ]);

  return (
    <TextButton
      decoration="underline"
      size="small"
      color="gray"
      onPress={handlePress}
    >
      내 사이즈 삭제
    </TextButton>
  );
};
